import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'automations',
  slug: 'automations',
  name: 'Automations',
  description: 'The trigger/condition/action workflow engine: build custom rules from scratch, install curated starter recipes, and tie auto-remediation scripts to alerts.',
  icon: 'Bolt',
  endpoints: [
    {
      slug: 'list',
      method: 'GET',
      path: '/api/automations',
      summary: 'List automation rules',
      description: 'Returns every automation rule configured for the current tenant, each with its trigger, conditions, actions, and run stats (runCount, lastRun, lastError, lastMatch).',
      auth: 'session',
      responseExample: `[
  {
    "id": "aut_7f3c",
    "name": "Notify on High priority ticket",
    "enabled": true,
    "kind": "ticket",
    "trigger": { "event": "ticket.created", "cadenceMins": 0 },
    "conditionMode": "all",
    "conditions": [{ "field": "priority", "op": "eq", "value": "High" }],
    "actions": [{ "type": "notify", "params": { "message": "New high-priority ticket: {{title}}" } }],
    "stopOnMatch": false,
    "runCount": 12,
    "lastRun": "2026-07-19T22:04:11.000Z",
    "lastError": "",
    "lastMatch": "2026-07-19T22:04:11.000Z",
    "createdAt": "2026-06-01T15:20:00.000Z",
    "updatedAt": "2026-07-10T09:11:32.000Z"
  }
]`,
      sourceFile: 'routes/automations.js',
    },
    {
      slug: 'meta',
      method: 'GET',
      path: '/api/automations/meta',
      summary: 'Get the rule-builder vocabulary',
      description: 'Returns the fixed lists of triggers, condition operators, and action types the engine accepts. Used to populate the rule-builder UI and to validate a rule client-side before submitting.',
      auth: 'session',
      responseExample: `{
  "triggers": ["ticket.created", "ticket.updated", "project.created", "project.updated", "device.created", "device.updated", "quote.created", "contract.created", "sync.completed", "schedule"],
  "ops": ["eq", "ne", "contains", "not_contains", "gt", "lt", "gte", "lte", "in", "empty", "not_empty", "changed", "regex"],
  "actions": ["set_field", "assign", "set_stage", "add_tag", "add_note", "add_reply", "notify", "email", "create_ticket", "webhook", "run_script"]
}`,
      sourceFile: 'routes/automations.js, lib/automations.js',
    },
    {
      slug: 'create',
      method: 'POST',
      path: '/api/automations',
      summary: 'Create an automation rule',
      description: 'Creates a rule from a trigger event, a set of conditions (all/any), and an ordered list of actions. The body is sanitized against the fixed trigger/op/action vocabulary via cleanAutomation before it is stored, so unknown values silently fall back to safe defaults rather than erroring.',
      auth: 'session',
      requestBodyExample: `{
  "name": "Assign new device tickets to onboarding queue",
  "enabled": true,
  "kind": "ticket",
  "trigger": { "event": "ticket.created" },
  "conditionMode": "all",
  "conditions": [{ "field": "type", "op": "eq", "value": "Onboarding" }],
  "actions": [{ "type": "assign", "params": { "queue": "onboarding" } }],
  "stopOnMatch": false
}`,
      responseExample: `{
  "id": "aut_9b21",
  "name": "Assign new device tickets to onboarding queue",
  "enabled": true,
  "kind": "ticket",
  "trigger": { "event": "ticket.created", "cadenceMins": 0 },
  "conditionMode": "all",
  "conditions": [{ "field": "type", "op": "eq", "value": "Onboarding" }],
  "actions": [{ "type": "assign", "params": { "queue": "onboarding" } }],
  "stopOnMatch": false,
  "runCount": 0,
  "lastRun": "",
  "lastError": "",
  "lastMatch": "",
  "createdAt": "2026-07-20T09:00:00.000Z",
  "updatedAt": "2026-07-20T09:00:00.000Z"
}`,
      sourceFile: 'routes/automations.js, lib/automations.js',
    },
    {
      slug: 'simulate',
      method: 'POST',
      path: '/api/automations/simulate',
      summary: 'Dry-run a rule against a sample entity',
      description: 'Evaluates a rule (either an existing rule id\'s definition or an inline rule body) against a sample entity with no side effects: no actions run, nothing is saved. Used by the rule-builder to preview whether a given ticket/device/etc. would match before enabling the rule.',
      auth: 'session',
      requestBodyExample: `{
  "rule": {
    "trigger": { "event": "ticket.updated" },
    "conditionMode": "all",
    "conditions": [{ "field": "status", "op": "eq", "value": "Resolved" }]
  },
  "entity": { "id": "tkt_8f2a", "status": "Resolved", "priority": "High" }
}`,
      responseExample: `{ "matched": true, "actionsPreview": [{ "type": "notify", "params": { "message": "Ticket resolved" } }] }`,
      sourceFile: 'routes/automations.js, lib/automations.js',
    },
    {
      slug: 'run-now',
      method: 'POST',
      path: '/api/automations/:id/run-now',
      summary: 'Run a rule now against a real or sample entity',
      description: 'Manually fires one rule for testing: pass entityId to load an existing record from the rule\'s collection (tickets, projects, devices, quotes, or contracts, inferred from the trigger event), or pass a sample entity inline. If the entity matches, the rule\'s actions actually run and runCount/lastRun/lastMatch are updated -- this has real side effects, unlike simulate.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Automation rule id.' }],
      requestBodyExample: `{ "entityId": "tkt_8f2a" }`,
      responseExample: `{
  "matched": true,
  "entity": { "id": "tkt_8f2a", "status": "Resolved", "priority": "High" }
}`,
      sourceFile: 'routes/automations.js, lib/automations.js',
    },
    {
      slug: 'library-list',
      method: 'GET',
      path: '/api/automations/library',
      summary: 'List starter automation recipes',
      description: 'Returns the curated catalog of starter recipes (lib/automation-library.js), each flagged installed:true if a rule with a matching libKey or name already exists in this tenant. Registered ahead of the generic /:id CRUD route so this path is never captured by it.',
      auth: 'session',
      responseExample: `[
  {
    "key": "ticket-high-priority-notify",
    "title": "Notify team on High priority ticket",
    "category": "Ticketing",
    "trigger": "ticket.created",
    "actions": ["notify"],
    "installed": false
  }
]`,
      sourceFile: 'routes/automationlibrary.js, lib/automation-library.js',
    },
    {
      slug: 'library-categories',
      method: 'GET',
      path: '/api/automations/library/categories',
      summary: 'Get starter-recipe category counts',
      description: 'Returns each recipe category with a count, for the library picker\'s category filter.',
      auth: 'session',
      responseExample: `[
  { "category": "Backup", "count": 3 },
  { "category": "Ticketing", "count": 6 }
]`,
      sourceFile: 'routes/automationlibrary.js',
    },
    {
      slug: 'library-install',
      method: 'POST',
      path: '/api/automations/library/install',
      summary: 'Install starter recipes',
      description: 'Installs recipes selected by {all:true}, {keys:[...]}, or {categories:[...]}. Idempotent -- a recipe already installed (matched by libKey or title) is skipped, not duplicated. Every installed recipe is forced to enabled:false regardless of the seed and re-validated against the live engine vocabulary via cleanAutomation before it is saved, so an admin must explicitly turn each one on.',
      auth: 'session',
      requestBodyExample: `{ "categories": ["Ticketing"] }`,
      responseExample: `{
  "installed": 5,
  "skipped": 1,
  "items": [
    { "title": "Notify team on High priority ticket", "status": "installed" },
    { "title": "Auto-tag onboarding tickets", "status": "skipped" }
  ],
  "available": 24
}`,
      sourceFile: 'routes/automationlibrary.js, lib/automation-library.js',
    },
    {
      slug: 'remediation-list',
      method: 'GET',
      path: '/api/remediation',
      summary: 'List auto-remediation rules',
      description: 'Returns every auto-remediation rule for the tenant, newest first, each annotated with scriptName resolved from the linked scriptId (or "(missing script)" if the script was deleted). These rules match incoming alerts by category/source/severity/title and run a script against the affected device (or all devices) when they hit.',
      auth: 'session',
      responseExample: `[
  {
    "id": "rem_4a1e",
    "name": "Restart spooler on printer alerts",
    "enabled": true,
    "scriptId": "scr_2c90",
    "scriptName": "Restart Print Spooler",
    "target": "device",
    "match": { "category": "Printer", "source": "", "minSeverity": "medium", "titleContains": "spooler" },
    "fireCount": 4,
    "lastFired": "2026-07-18T03:12:00.000Z",
    "createdAt": "2026-05-02T12:00:00.000Z",
    "updatedAt": "2026-05-02T12:00:00.000Z"
  }
]`,
      sourceFile: 'routes/remediation.js',
    },
    {
      slug: 'remediation-create',
      method: 'POST',
      path: '/api/remediation',
      summary: 'Create an auto-remediation rule',
      description: 'Creates a rule that matches alerts by category (must be one of the fixed alert CATEGORIES), source, minimum severity, and/or a title substring, and runs the given scriptId when an alert matches. scriptId must reference an existing script or the request is rejected.',
      auth: 'session',
      requestBodyExample: `{
  "name": "Restart spooler on printer alerts",
  "enabled": true,
  "scriptId": "scr_2c90",
  "target": "device",
  "match": { "category": "Printer", "minSeverity": "medium", "titleContains": "spooler" }
}`,
      responseExample: `{
  "id": "rem_4a1e",
  "name": "Restart spooler on printer alerts",
  "enabled": true,
  "scriptId": "scr_2c90",
  "scriptName": "Restart Print Spooler",
  "target": "device",
  "match": { "category": "Printer", "source": "", "minSeverity": "medium", "titleContains": "spooler" },
  "fireCount": 0,
  "lastFired": "",
  "createdAt": "2026-07-20T09:00:00.000Z",
  "updatedAt": "2026-07-20T09:00:00.000Z"
}`,
      sourceFile: 'routes/remediation.js',
    },
  ],
};
